import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Stock } from 'src/app/models/stock';
import { StockAssignment } from 'src/app/models/stock-assignment';
import { User } from 'src/app/models/user';
import { InventoryService } from 'src/app/services/inventory.service';
import { SettlementsService } from 'src/app/services/settlements.service';

@Component({
  selector: 'app-modal-stock-assignment',
  template: `
    <ng-template #modalStockAssignment let-modal>
      <div class="modal-header">
        <h5 class="modal-title">
          {{ isEditMode ? 'Editar Asignación' : 'Nueva Asignación de Inventario' }}
        </h5>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
      </div>
      
      <div class="modal-body">
        <form [formGroup]="assignmentForm" (ngSubmit)="onSubmit()">
          <!-- User Selection -->
          <div class="mb-3" *ngIf="users">
            <label for="userId" class="form-label">Usuario <span class="text-danger">*</span></label>
            <select 
              id="userId" 
              class="form-select" 
              formControlName="userId"
              [class.is-invalid]="assignmentForm.get('userId')?.invalid && assignmentForm.get('userId')?.touched"
            >
              <option value="">Seleccionar usuario...</option>
              <option *ngFor="let user of users" [value]="user.id">
                {{ user.name }} - {{ user.email }}
              </option>
            </select>
            <div class="invalid-feedback" *ngIf="assignmentForm.get('userId')?.invalid && assignmentForm.get('userId')?.touched">
              <div *ngIf="assignmentForm.get('userId')?.errors?.['required']">
                El usuario es requerido
              </div>
            </div>
          </div>

          <!-- Item Selection -->
          <div class="mb-3" *ngIf="items && items.length > 0">
            <label for="itemId" class="form-label">Artículo <span class="text-danger">*</span></label>
            <select 
              id="itemId" 
              class="form-select" 
              formControlName="itemId"
              (change)="onItemChange()"
              [class.is-invalid]="assignmentForm.get('itemId')?.invalid && assignmentForm.get('itemId')?.touched"
            >
              <option value="">Seleccionar artículo...</option>
              <option *ngFor="let item of items" [value]="item.id">
                {{ item.name }} - {{ item.sku }} (Disponible: {{ item.quantity }})
              </option>
            </select>
            <div class="invalid-feedback" *ngIf="assignmentForm.get('itemId')?.invalid && assignmentForm.get('itemId')?.touched">
              <div *ngIf="assignmentForm.get('itemId')?.errors?.['required']">
                El artículo es requerido
              </div>
            </div>
          </div>

          <!-- Selected Item Info -->
          <div *ngIf="selectedItem" class="alert alert-info mb-3">
            <div class="d-flex align-items-center">
              <i class="fas fa-info-circle me-2"></i>
              <div>
                <strong>{{ selectedItem.name }}</strong><br>
                <small>SKU: {{ selectedItem.sku }} | Categoría: {{ selectedItem.unit_of_measure }}</small><br>
                <small>Cantidad disponible: <span class="badge bg-primary">{{ selectedItem.quantity }}</span></small>
              </div>
            </div>
          </div>

          <!-- Quantity -->
          <div class="mb-3">
            <label for="quantity" class="form-label">Cantidad <span class="text-danger">*</span></label>
            <input 
              type="number" 
              id="quantity" 
              class="form-control" 
              formControlName="quantity"
              min="1"
              [max]="selectedItem?.quantity || 999"
              [class.is-invalid]="assignmentForm.get('quantity')?.invalid && assignmentForm.get('quantity')?.touched"
            >
            <div class="invalid-feedback" *ngIf="assignmentForm.get('quantity')?.invalid && assignmentForm.get('quantity')?.touched">
              <div *ngIf="assignmentForm.get('quantity')?.errors?.['required']">
                La cantidad es requerida
              </div>
              <div *ngIf="assignmentForm.get('quantity')?.errors?.['min']">
                La cantidad debe ser mayor a 0
              </div>
              <div *ngIf="assignmentForm.get('quantity')?.errors?.['max']">
                La cantidad no puede ser mayor a {{ selectedItem?.quantity }}
              </div>
            </div>
          </div>

          <!-- Assignment Date -->
          <div class="mb-3">
            <label for="assignedDate" class="form-label">Fecha de Asignación <span class="text-danger">*</span></label>
            <input 
              type="datetime-local" 
              id="assignedDate" 
              class="form-control" 
              formControlName="assignedDate"
              [class.is-invalid]="assignmentForm.get('assignedDate')?.invalid && assignmentForm.get('assignedDate')?.touched"
            >
            <div class="invalid-feedback" *ngIf="assignmentForm.get('assignedDate')?.invalid && assignmentForm.get('assignedDate')?.touched">
              <div *ngIf="assignmentForm.get('assignedDate')?.errors?.['required']">
                La fecha de asignación es requerida
              </div>
            </div>
          </div>

          <!-- Status (for edit mode) -->
          <div class="mb-3" *ngIf="isEditMode">
            <label for="status" class="form-label">Estado <span class="text-danger">*</span></label>
            <select 
              id="status" 
              class="form-select" 
              formControlName="status"
              [class.is-invalid]="assignmentForm.get('status')?.invalid && assignmentForm.get('status')?.touched"
            >
              <option value="active">Activo</option>
              <option value="returned">Devuelto</option>
              <option value="damaged">Dañado</option>
            </select>
          </div>

          <!-- Notes -->
          <div class="mb-3">
            <label for="notes" class="form-label">Notas</label>
            <textarea 
              id="notes" 
              class="form-control" 
              formControlName="notes"
              rows="3"
              placeholder="Notas adicionales sobre la asignación..."
            ></textarea>
          </div>

          <!-- Loading State -->
          <div *ngIf="loading" class="text-center mb-3">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Cargando...</span>
            </div>
          </div>

          <!-- Error Message -->
          <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div *ngIf="successMessage" class="alert alert-success" role="alert">
            <i class="fas fa-check-circle me-2"></i>
            {{ successMessage }}
          </div>
        </form>
      </div>
      
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
          Cancelar
        </button>
        <button 
          type="button" 
          class="btn btn-primary" 
          (click)="onSubmit()"
          [disabled]="assignmentForm.invalid || loading"
        >
          <span *ngIf="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          {{ isEditMode ? 'Actualizar' : 'Crear' }} Asignación
        </button>
      </div>
    </ng-template>
  `,
  styles: `
    .modal-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #dee2e6;
    }
    
    .modal-title {
      font-weight: 600;
      color: #2c3e50;
    }
    
    .form-label {
      font-weight: 500;
      color: #495057;
    }
    
    .text-danger {
      color: #dc3545 !important;
    }
    
    .alert {
      border: none;
      border-radius: 8px;
    }
    
    .alert-info {
      background-color: #e7f3ff;
      color: #0c5460;
    }
    
    .alert-danger {
      background-color: #f8d7da;
      color: #721c24;
    }
    
    .alert-success {
      background-color: #d4edda;
      color: #155724;
    }
    
    .badge {
      font-size: 0.75em;
    }
    
    .form-control:focus,
    .form-select:focus {
      border-color: #0d6efd;
      box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
    }
    
    .invalid-feedback {
      display: block;
    }
    
    .btn {
      border-radius: 6px;
      font-weight: 500;
    }
    
    .modal-footer {
      background-color: #f8f9fa;
      border-top: 1px solid #dee2e6;
    }
  `
})
export class ModalStockAssignmentComponent implements AfterViewInit {

  @ViewChild('modalStockAssignment') modalStockAssignment !: ElementRef;
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @Output() assignmentCreated: EventEmitter<any> = new EventEmitter<any>();
  @Output() assignmentUpdated: EventEmitter<any> = new EventEmitter<any>();

  assignmentForm: FormGroup;
  users: User[] = [];
  items: Stock[] = [];
  selectedItem: Stock | null = null;
  
  loading = false;
  errorMessage = '';
  successMessage = '';
  isEditMode = false;
  currentAssignment: StockAssignment | null = null;

  private modalRef: NgbModalRef | undefined;

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private store: SettlementsService,
    private inventoryService: InventoryService
  ) { 
    this.assignmentForm = this.createForm();
  }

  ngAfterViewInit(): void {
      
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadItems();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      userId: ['', Validators.required],
      itemId: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      assignedDate: [this.getCurrentDateTime(), Validators.required],
      status: ['active'],
      notes: ['']
    });
  }

  private getCurrentDateTime(): string {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  }

  loadUsers(): void {
    this.store.getUsers({}).subscribe({
      next: (users) => {
        console.log('Users loaded:', users);
        this.users = users.data;
      },
      error: (error) => {
        console.error('Error loading users:', error);
      }
    });
  }

  loadItems(): void {
    this.inventoryService.getStock({}).subscribe({
      next: (items: any) => {
        console.log('Items loaded:', items);
        this.items = items.data;
      },
      error: (error: any) => {
        console.error('Error loading items:', error);
      }
    });
  }

  onItemChange(): void {
    const itemId = this.assignmentForm.get('itemId')?.value;
    this.selectedItem = this.items.find(item => item.id === parseInt(itemId)) || null;
    
    if (this.selectedItem) {
      // Update quantity validators based on available quantity
      const quantityControl = this.assignmentForm.get('quantity');
      quantityControl?.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(10)
      ]);
      quantityControl?.updateValueAndValidity();
    }
  }

  private populateForm(assignment: StockAssignment): void {
    this.assignmentForm.patchValue({
      userId: assignment.user.id,
      itemId: assignment.item.id,
      quantity: assignment.quantity,
      assignedDate: new Date(assignment.assignedDate).toISOString().slice(0, 16),
      status: assignment.status,
      notes: assignment.notes || ''
    });
    
    // Set selected item
    this.selectedItem = this.items.find(item => item.id === assignment.item.id) || null;
  }

  private resetForm(): void {
    this.assignmentForm.reset({
      userId: '',
      itemId: '',
      quantity: 1,
      assignedDate: this.getCurrentDateTime(),
      status: 'active',
      notes: ''
    });
    
    this.selectedItem = null;
    this.errorMessage = '';
    this.successMessage = '';
    this.loading = false;
  }

  onSubmit(): void {
    if (this.assignmentForm.valid && !this.loading) {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const formData = {
        ...this.assignmentForm.value,
        userId: parseInt(this.assignmentForm.value.userId),
        itemId: parseInt(this.assignmentForm.value.itemId),
        assignedDate: new Date(this.assignmentForm.value.assignedDate)
      };

      const request = this.isEditMode && this.currentAssignment
        ? this.inventoryService.assignStockToUser(formData)
        : this.inventoryService.assignStockToUser(formData);

      request.subscribe({
        next: (response: any) => {
          this.loading = false;
          this.successMessage = this.isEditMode 
            ? 'Asignación actualizada exitosamente'
            : 'Asignación creada exitosamente';
          
          setTimeout(() => {
            if (this.isEditMode) {
              this.assignmentUpdated.emit(response);
            } else {
              this.assignmentCreated.emit(response);
            }
            this.dismiss();
          }, 1500);
        },
        error: (error: any) => {
          this.loading = false;
          this.errorMessage = error.error?.message || 'Error al procesar la asignación';
          console.error('Error:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.assignmentForm.controls).forEach(key => {
        this.assignmentForm.get(key)?.markAsTouched();
      });
    }
  }

  openModal(modalContent: TemplateRef<any>) {
    console.log('openModal modalBuilding');
    this.modalRef = this.modalService.open(modalContent);
  }

  dismiss() {
    if (this.modalRef) {
      this.updateDismiss.emit();
      this.modalRef.dismiss();
    }
  }

}
